import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Center } from "@freightsimple/ui-core/PageLayout/Center";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Spinner } from "@freightsimple/ui-core/Feedback/Spinner";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { ArrowLeftIcon } from "@phosphor-icons/react/ArrowLeft";
import { ArrowRightIcon } from "@phosphor-icons/react/ArrowRight";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useShipmentService } from "@/Services/ShipmentService";
import { ShipmentPdfRenderer } from "@/Shared/Shipment/CustomComponents/ShipmentPdfRenderer";
import { ShipmentDocumentDownloadButton } from "@/Shared/Shipment/CustomComponents/ShipmentDocumentDownloadButton";

interface ViewShipmentsDocumentViewerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId: string;
  documentDescription: string;
  onDownloadDocument: () => Promise<void>;
}

// Full-size PDF viewer dialog. Title, paging and download ride in the dialog's
// own header — the viewer used to draw a second header of its own underneath
// the dialog's, with a duplicate close button.
function ViewShipmentsDocumentViewerDialog_(
  props: ViewShipmentsDocumentViewerDialogProps,
) {
  const {
    documentId,
    documentDescription,
    onDownloadDocument,
    open,
    onOpenChange,
  } = props;
  const { getDocumentPdf } = useShipmentService();
  const [base64Data, setBase64Data] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const hasLoaded = useRef(false);

  const loadDocument = useCallback(async () => {
    if (!documentId) return;

    setIsLoading(true);
    setError(undefined);

    try {
      const response = await getDocumentPdf(documentId);
      setBase64Data(response.base64EncodedPdf);
    } catch (err) {
      setError("Failed to load document");
      console.error("Error loading document:", err);
    } finally {
      setIsLoading(false);
    }
  }, [documentId, getDocumentPdf]);

  useEffect(() => {
    if (open && documentId && !hasLoaded.current) {
      hasLoaded.current = true;
      loadDocument();
    }
  }, [open, documentId, loadDocument]);

  // Nothing to page through or download until the document is actually here.
  const documentReady =
    !isLoading && base64Data !== undefined && error === undefined;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="wide"
      title={documentDescription}
      headerActions={
        documentReady ? (
          <>
            {totalPages > 1 && (
              <>
                <Button
                  icon={ArrowLeftIcon}
                  tooltip="Previous page"
                  data-testid="previous-page-button"
                  disabled={currentPage <= 1}
                  disabledMessage="Already on the first page"
                  onClick={() => setCurrentPage((page) => page - 1)}
                />
                <T size="sm" color="secondary">
                  Page {currentPage} of {totalPages}
                </T>
                <Button
                  icon={ArrowRightIcon}
                  tooltip="Next page"
                  data-testid="next-page-button"
                  disabled={currentPage >= totalPages}
                  disabledMessage="Already on the last page"
                  onClick={() => setCurrentPage((page) => page + 1)}
                />
              </>
            )}
            <ShipmentDocumentDownloadButton
              data-testid="modal-download-button"
              onDownloadDocument={onDownloadDocument}
            />
          </>
        ) : undefined
      }
    >
      {isLoading ? (
        // Hold the dialog at roughly the height of a rendered page so it
        // doesn't collapse and then jump when the PDF arrives.
        <Center minHeight={400}>
          <Row justify="center">
            <Spinner size={48} />
          </Row>
        </Center>
      ) : (
        <Stack gap={0} align="center">
          {error !== undefined && <Banner variant="danger">{error}</Banner>}
          {base64Data !== undefined && error === undefined && (
            <ShipmentPdfRenderer
              base64Data={base64Data}
              currentPage={currentPage}
              onTotalPagesChange={setTotalPages}
            />
          )}
        </Stack>
      )}
    </Dialog>
  );
}

interface ShowDocumentViewerParams {
  documentId: string;
  documentDescription: string;
  onDownloadDocument: () => Promise<void>;
}

// Declarative replacement for the imperative useDocumentViewerDialog.
export function useDocumentViewerDialog(): {
  show: (params: ShowDocumentViewerParams) => void;
  dialogs: ReactNode;
} {
  const [open, setOpen] = useState(false);
  const [openCount, setOpenCount] = useState(0);
  const [params, setParams] = useState<ShowDocumentViewerParams | undefined>(
    undefined,
  );

  function show(viewerParams: ShowDocumentViewerParams) {
    setParams(viewerParams);
    setOpenCount((count) => count + 1);
    setOpen(true);
  }

  const dialogs = params !== undefined && (
    <ViewShipmentsDocumentViewerDialog_
      key={`shipment-document-viewer-${openCount}`}
      open={open}
      onOpenChange={setOpen}
      documentId={params.documentId}
      documentDescription={params.documentDescription}
      onDownloadDocument={params.onDownloadDocument}
    />
  );

  return { show, dialogs };
}
